import React, { useState } from 'react'
import { ShareBar } from '../components/editorial/ShareBar'
import { QuoteBox } from '../components/editorial/QuoteBox'
import { CtaSection } from '../components/editorial/CtaSection'
import { TrustBadges } from '../components/editorial/TrustBadges'
import {
  Star,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  ArrowRight,
} from 'lucide-react'
export function Advertorial() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index)
  }
  return (
    <div className="min-h-screen bg-[#fafaf9] font-sans text-gray-800">
      {/* Top Banner */}
      <div className="bg-black text-white text-center py-2 text-xs font-bold tracking-widest uppercase">
        Advertorial • Trending in Health & Wellness
      </div>

      <main className="max-w-3xl mx-auto px-4 py-8 md:py-12">
        {/* Headline Section */}
        <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-4">
          Women Over 40: If Your Stomach Stays Puffy All Day, Your Lymphatic
          System May Have 'Shut Down'
        </h1>

        <h2 className="text-xl md:text-2xl text-gray-600 font-medium mb-8 leading-relaxed">
          Physical Therapist Reveals: The Hidden Reason Your Belly Won't Flatten
          (Even When You Exercise and Eat Right)
        </h2>

        {/* Author Byline */}
        <div className="flex items-center gap-4 mb-1">
          <img
            src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&q=80&w=100&h=100"
            alt="Dr. Michelle Torres"
            className="w-12 h-12 rounded-full object-cover border border-gray-200"
          />
          <div>
            <p className="text-sm font-bold text-gray-900">
              By Dr. Michelle Torres, PT, DPT
            </p>
            <p className="text-xs text-gray-500">
              Physical Therapy & Lymphatic Health Specialist • April 2025
            </p>
          </div>
        </div>

        {/* Article Stats */}
        <div className="mb-6">
          <img
            src="https://cdn.magicpatterns.com/uploads/vLS54wRFWi3wjuwPk7mDxu/image.png"
            alt="2 min read • 43,287 Views | Rated 'Excellent' Based on 1,200+ Reviews"
            className="w-full"
          />
        </div>

        {/* Hero Image */}
        <div className="mb-2 -mx-4 md:-mx-12 lg:-mx-2">
          <img
            src="https://cdn.magicpatterns.com/uploads/d6nJSkNWci7oYGVnMA9naE/Beige_Aesthetic_Skincare_Banner_Landscape_(1).svg"
            alt="Before and after lymphatic drainage results"
            className="w-full h-auto object-cover"
          />
          <p className="text-xs text-gray-500 mt-2 italic px-4 md:px-12 lg:px-24">
            Many women over 40 mistake lymphatic fluid retention for belly fat.
          </p>
        </div>

        <ShareBar />

        {/* Article Body */}
        <article className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-p:text-gray-700 prose-p:leading-loose">
          <p className="font-medium text-xl text-gray-900">
            If your belly stays puffy no matter what you try—this 10-minute
            at-home treatment could finally give you the flat, firm midsection
            you deserve.
          </p>

          <p>
            Hi, I'm Dr. Michelle Torres, and I'm a licensed physical therapist
            specializing in women's health and lymphatic dysfunction.
          </p>

          <p>
            Over my 18 years in practice, I've worked with over 2,300 women
            struggling with stubborn belly bloat, post-pregnancy pooch, and that
            frustrating "puffy all day" feeling that diet and exercise just
            won't fix.
          </p>

          <p>
            And I'm going to share something with you that most doctors won't
            tell you...
          </p>

          <p>
            Your problem isn't "water weight." It's not stubborn fat. And it's
            definitely not lack of willpower.
          </p>

          <p>
            It's something called <strong>Lymphatic Shutdown</strong>—and it's
            affecting millions of women over 40 right now.
          </p>

          <div className="my-8 flex justify-center">
            <img
              src="https://cdn.magicpatterns.com/uploads/3ZBhVQVmmhhKTq7MPeJDiB/ChatGPT_Image_Dec_15,_2025,_07_24_51_PM.png"
              alt="Lymphatic system diagram showing apron belly and compression of lymph nodes"
              className="w-1/2 rounded-lg shadow-sm"
            />
          </div>

          <h3 className="text-2xl mt-10 mb-4">
            The Shocking Truth About Why Your Stomach Stays Puffy
          </h3>

          <p>Here's what most women don't realize:</p>

          <p>
            After age 40, your lymphatic system—the network of vessels
            responsible for draining excess fluid and waste from your
            tissues—starts to slow down dramatically.
          </p>

          <p>
            Think of it like this: Your lymph vessels are supposed to pump fluid
            through your body 24/7, keeping you lean and defined.
          </p>

          <p>
            But as you age, those vessels become sluggish. They stop contracting
            efficiently. And over months and years, fluid starts backing up.
          </p>

          <QuoteBox title="Medical Insight">
            "By age 45, lymphatic vessel contraction efficiency decreases by up
            to 60%. This creates what we call 'lymphatic traffic jam'—years of
            backed-up fluid that's literally thickened into a gel-like
            consistency."
            <br />
            <br />
            <span className="text-base not-italic font-bold">
              — Dr. Robert Chen, Leading Researcher in Lymphatic Health
            </span>
          </QuoteBox>

          <h3 className="text-2xl mt-8 mb-3">
            Why Traditional Weight Loss Methods Don't Work
          </h3>

          <p className="mb-3">You've probably tried everything:</p>

          <div className="flex flex-col md:flex-row gap-4 items-center my-4">
            <ul className="list-none space-y-2 pl-0 my-0 md:flex-[0.5]">
              <li className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-sm font-bold">
                  ✕
                </span>
                Cutting carbs and sugar
              </li>
              <li className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-sm font-bold">
                  ✕
                </span>
                Drinking more water
              </li>
              <li className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-sm font-bold">
                  ✕
                </span>
                Doing hundreds of crunches
              </li>
              <li className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-sm font-bold">
                  ✕
                </span>
                Expensive compression garments
              </li>
            </ul>

            <div className="md:flex-[0.5]">
              <img
                src="https://cdn.magicpatterns.com/uploads/xpdHNeJhKKqaWJVg6Zmw9M/image.jpg"
                alt="Lymphatic system illustration"
                className="w-full rounded-lg shadow-sm"
              />
            </div>
          </div>

          <p>
            Maybe you even lost weight on the scale... but your belly still
            looked puffy and undefined. That's because you were attacking the
            wrong problem.
          </p>

          <p>
            You weren't dealing with fat. You were dealing with years of
            backed-up lymphatic fluid that had nowhere to go. And unless you
            restart your lymphatic system's pumping function, that fluid will
            stay trapped forever.
          </p>

          <h3 className="text-2xl mt-6 mb-2">The Moment Everything Changed</h3>

          <p className="mb-3">
            About two years ago, I was struggling with this exact problem
            myself. At 48, after having two kids, my stomach stayed puffy all
            day long. I'd wake up relatively flat, but by noon I looked six
            months pregnant.
          </p>

          <div className="my-4 grid grid-cols-2 gap-4">
            <img
              src="https://cdn.magicpatterns.com/uploads/bmsWETrtUMhwbqUcUcPbdW/Untitled_design_(2).svg"
              alt="Before - bloated stomach"
              className="rounded-lg shadow-sm w-full object-cover"
            />
            <img
              src="https://cdn.magicpatterns.com/uploads/o7BD67dANazLaFaWQXFznu/Untitled_design_(3).svg"
              alt="After - flat stomach"
              className="rounded-lg shadow-sm w-full object-cover"
            />
            <p className="col-span-2 text-center text-sm text-gray-500 italic">
              Left: My stomach at 48. Right: After retraining my lymphatic
              system.
            </p>
          </div>

          <p>
            As a physical therapist, I knew the science behind lymphatic
            drainage. I'd recommended manual lymphatic massage to patients for
            years. But those $200 sessions only provided temporary relief.
          </p>

          <p>
            That's when I partnered with Phlora to create something
            revolutionary.
          </p>

          <div className="bg-gray-50 border border-gray-200 p-8 rounded-xl my-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 mt-0 text-center">
              Introducing: The Phlora 5-in-1 Lymphatic Drainage Body Sculptor
            </h3>

            <div className="flex justify-center mb-6">
              <img
                src="https://cdn.magicpatterns.com/uploads/5Gj3UzfMzSbQwCm32HGUT5/ChatGPT_Image_Dec_15,_2025,_07_11_47_PM.png"
                alt="Phlora Device"
                className="w-1/2 rounded-lg"
              />
            </div>

            <p className="mb-6">
              After 14 months of development, we finally created a device that
              brings professional-grade lymphatic restoration into your home.
              Here's what makes it different:
            </p>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold shrink-0">
                  1
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-lg m-0">
                    Precision Heat (40-42°C)
                  </h4>
                  <p className="text-gray-600 m-0 text-base">
                    Penetrates 3-5mm deep to literally liquefy gel-like lymph
                    back into flowing state. Think of it like warming up cold
                    honey.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold shrink-0">
                  2
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-lg m-0">
                    Bio-Pulse EMS
                  </h4>
                  <p className="text-gray-600 m-0 text-base">
                    Delivers gentle micro-currents that cause lymph vessel walls
                    to contract—teaching them to remember how to pump
                    independently.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold shrink-0">
                  3
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-lg m-0">
                    Lymphatic Resonance Vibration
                  </h4>
                  <p className="text-gray-600 m-0 text-base">
                    Matches your body's natural lymph vessel contraction rhythm
                    (8-12Hz).
                  </p>
                </div>
              </div>
            </div>
          </div>

          <h3 className="text-2xl mt-10 mb-6">
            The Results Speak for Themselves
          </h3>

          <div className="grid md:grid-cols-2 gap-6 my-8 not-prose">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
              <div className="flex items-center gap-1 text-yellow-400 mb-3">
                <Star fill="currentColor" size={16} />
                <Star fill="currentColor" size={16} />
                <Star fill="currentColor" size={16} />
                <Star fill="currentColor" size={16} />
                <Star fill="currentColor" size={16} />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">
                "Lost 4 Inches in 3 Weeks"
              </h4>
              <p className="text-gray-600 text-sm mb-4">
                "I'm 52 and had given up on ever having a flat stomach again.
                After just 10 days with Phlora, my pants fit better. After 3
                weeks, I'd lost 4 inches from my waist."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=100"
                    alt="User"
                  />
                </div>
                <span className="text-xs font-bold text-gray-500">
                  Jennifer M., Phoenix, AZ
                </span>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
              <div className="flex items-center gap-1 text-yellow-400 mb-3">
                <Star fill="currentColor" size={16} />
                <Star fill="currentColor" size={16} />
                <Star fill="currentColor" size={16} />
                <Star fill="currentColor" size={16} />
                <Star fill="currentColor" size={16} />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">
                "Doctor Was Shocked"
              </h4>
              <p className="text-gray-600 text-sm mb-4">
                "My doctor kept asking what procedure I'd had done because my
                stomach looked so much flatter and firmer. When I told her it
                was an at-home device, she was shocked!"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=100"
                    alt="User"
                  />
                </div>
                <span className="text-xs font-bold text-gray-500">
                  Patricia L., Denver, CO
                </span>
              </div>
            </div>
          </div>

          <h3 className="text-2xl mt-10 mb-4">
            How to Use Phlora (It's Easier Than You Think)
          </h3>

          <div className="flex flex-col md:flex-row gap-6 my-6 items-center">
            {/* LEFT: Steps */}
            <div className="flex-1">
              <ol className="space-y-4 list-decimal pl-5">
                <li className="pl-2">Apply conductive gel to treatment area</li>
                <li className="pl-2">
                  Power on device and select intensity level
                </li>
                <li className="pl-2">Glide in upward strokes for 10 minutes</li>
                <li className="pl-2">Repeat 5–7 times per week</li>
              </ol>
            </div>

            {/* RIGHT: Image */}
            <div className="flex-1 flex justify-center">
              <img
                src="https://cdn.shopify.com/s/files/1/0785/9532/6211/files/Gemini_Generated_Image_lx24xolx24xolx24.png?v=1765922140"
                alt="How to use Phlora device"
                className="max-w-full h-auto rounded-xl shadow-md"
                loading="lazy"
              />
            </div>
          </div>

          <CtaSection />

          <div className="not-prose my-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h3>
            <div className="space-y-2">
              {[
                {
                  q: 'How long does it take to see results?',
                  a: "Most women notice feeling 'lighter' and less bloated within 24-48 hours. Visible changes like reduced measurements and smoother skin typically appear within 2-3 weeks.",
                },
                {
                  q: 'Is it safe to use during pregnancy?',
                  a: 'We recommend consulting your OB-GYN before use during pregnancy, though many women safely use it postpartum to address retained fluid.',
                },
                {
                  q: 'Will the results last?',
                  a: 'Yes! Unlike temporary massage that just moves fluid around, Phlora retrains your vessels to function correctly. After 6-8 weeks, most women can maintain results with just 2-3 sessions per week.',
                },
                {
                  q: 'Can I use it on other areas besides my belly?',
                  a: 'Absolutely! It works on thighs, arms, buttocks, back—anywhere you have puffiness, cellulite, or loose skin from backed-up lymph.',
                },
                {
                  q: "What if it doesn't work for me?",
                  a: "You have 90 days to try it completely risk-free. If you're not satisfied for any reason, contact our customer service team for a full refund.",
                },
              ].map((faq, idx) => (
                <div
                  key={idx}
                  className="border border-gray-200 rounded-lg bg-white overflow-hidden"
                >
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full flex items-center justify-between p-4 text-left font-bold text-gray-900 hover:bg-gray-50"
                  >
                    {faq.q}
                    {openFaq === idx ? (
                      <ChevronUp size={20} />
                    ) : (
                      <ChevronDown size={20} />
                    )}
                  </button>
                  {openFaq === idx && (
                    <div className="p-4 pt-0 text-gray-600 border-t border-gray-100 bg-gray-50">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <TrustBadges />

          <div className="text-center mt-12 mb-8">
            <a
              href="https://www.phloraskin.shop/products/electric-meridian-brush-massage-whole-body-shoulder-and-neck-dredging"
              className="bg-[#22c55e] hover:bg-[#16a34a] text-white text-xl font-bold py-4 px-12 rounded-lg shadow-lg transform transition hover:-translate-y-0.5 inline-flex items-center gap-2 no-underline"
            >
              GET 50% OFF PHLORA TODAY
              <ArrowRight size={24} />
            </a>
          </div>
        </article>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-gray-200 text-xs text-gray-500 text-center leading-relaxed">
          <p className="mb-4">
            Disclaimer: Individual results may vary. This device is not intended
            to diagnose, treat, cure, or prevent any disease. Consult your
            healthcare provider before starting any new wellness routine.
          </p>
          <p className="mb-4">
            Customer testimonials are real but may not reflect typical results.
            Photos and measurements shown are from individual users and are not
            guaranteed outcomes.
          </p>
          <p>&copy; 2025 Phlora Wellness. All rights reserved.</p>
        </footer>
      </main>
    </div>
  )
}
function ArrowRight({ size = 24 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  )
}
